import React, {
  useEffect, useMemo, useContext, useCallback
} from 'react';
import {
  string, bool, number, shape, arrayOf
} from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  ProductPod,
  ProductATC,
  ProductRating,
  ProductImage,
  PodFooter,
  PodSection,
  PodSpacer,
} from '@thd-olt-component-react/product-pod';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';
import './SponsoredProductPod.scss';
import { dataModel } from './SponsoredProductPod.dataModel';
import { isSafari } from '../../utils/utils';
import { useSponsoredProductPodTelemetry, useNodeRef } from '../../utils/hooks';

const SponsoredProductPod = ({
  product, storeId, position, noATCFulfillment
}) => {
  const experienceContext = useContext(ExperienceContext);

  // nodes
  const [productPodNodeRef, productPodNode] = useNodeRef();
  const [productPodRatingRef, productPodRatingNode] = useNodeRef();

  // --
  const isMobile = useMemo(
    () => experienceContext.channel === 'mobile',
    [experienceContext.channel]
  );

  // --
  const itcParams = useMemo(() => {
    // ---
    const campaignId = product?.info?.sponsoredMetadata?.campaignId || '';
    const placementId = product?.info?.sponsoredMetadata?.placementId || '';
    const slotId = product?.info?.sponsoredMetadata?.slotId || '';
    // ---

    return `ITC=AUC-${campaignId}-${placementId}-${slotId}`;
  }, [
    product?.info?.sponsoredMetadata?.campaignId,
    product?.info?.sponsoredMetadata?.placementId,
    product?.info?.sponsoredMetadata?.slotId,
  ]);

  // --
  const merchRec = useMemo(() => {
    return `MERCH=REC-_-sp-_-${product.itemId}-_-${position}-_-n/a-_-n/a-_-n/a-_-n/a-_-n/a`;
  }, [product.itemId, position]);

  // --
  const redirectUrl = useMemo(() => {
    // ---
    const protocol = window.location.protocol;
    const host = window.location.host;
    const canonicalUrl = product?.identifiers?.canonicalUrl;
    // ---

    return `${protocol}//${host}${canonicalUrl}?${merchRec}&${itcParams}`;
  }, [product?.identifiers?.canonicalUrl, itcParams, merchRec]);

  // --
  const onclickBeacon = useMemo(() => {
    return product?.info?.sponsoredBeacon?.onClickBeacons?.[0];
  }, [product?.info?.sponsoredBeacon?.onClickBeacons]);

  // --
  const atcBeacon = useMemo(() => {
    return onclickBeacon?.replace(/&redirect=$/, '');
  }, [onclickBeacon]);

  // -----------------
  // Telemetry
  // -----------------
  const {
    triggerClickOnAtcButton,
    triggerClickOnImage,
    triggerClickOnHeader,
    triggerClickOnRating,
  } = useSponsoredProductPodTelemetry({
    productPodNode,
    itemId: product?.itemId,
    trackSource: product?.info?.sponsoredMetadata?.trackSource,
    position,
    slotId: product?.info?.sponsoredMetadata?.slotId,
  });

  // -----------------
  // Handlers
  // -----------------
  /**
   * Handler on: atc 'click'
   */
  const handleClickOnProductAtc = useCallback(() => {
    // --
    triggerClickOnAtcButton(); // new-relic
    fetch(atcBeacon); // piq
  }, [atcBeacon, triggerClickOnAtcButton]);

  /**
   * Handler on: image 'click'
   */
  const handleClickOnProductImage = useCallback((event) => {
    event.preventDefault();
    // --
    triggerClickOnImage(); // new-relic
    window.location = redirectUrl; // redirect
  }, [redirectUrl, triggerClickOnImage]);

  /**
   * Handler on: header 'click'
   */
  const handleClickOnProductHeader = useCallback((event) => {
    event.preventDefault();
    // --
    triggerClickOnHeader(); // new-relic
    window.location = redirectUrl; // redirect
  }, [redirectUrl, triggerClickOnHeader]);

  /**
   * Handler on: rating 'click'
   */
  useEffect(() => {
    // check
    if (!productPodRatingNode) return undefined; // case: not mounted
    if (!redirectUrl) return undefined;

    const selector = `a[href*="${product?.itemId}"]`;
    const element = productPodRatingNode.querySelector(selector);

    // check
    if (!element) return undefined;

    // --
    const handler = (event) => {
      event.preventDefault();
      // --
      triggerClickOnRating(); // new-relic
      window.location = redirectUrl; // redirect
    };

    // install
    element.addEventListener('click', handler);
    // uninstall
    return () => {
      element.removeEventListener('click', handler);
    };
  }, [productPodRatingNode, redirectUrl, product?.itemId, triggerClickOnRating]);
  // ---- ----

  // -----------------
  // ProductPod
  // -----------------
  return (
    <div
      ref={productPodNodeRef}
      data-product-id={product.itemId}
      className="sponsored-product-pod"
      data-testid="sponsored-product-pod"
    >
      <ProductPod
        itemId={product.itemId}
        storeId={storeId}
        analyticsData={{ parent: 'sponsored-carousel', position: position - 1 }}
        render={(pod) => (
          <>
            <ProductImage
              itemId={pod.itemId}
              showSecondaryImage={pod.showSecondaryImage}
              onClick={handleClickOnProductImage}
            />
            <PodSection columnAlign alignTop>
              <div style={{ height: `${isSafari() ? '89px' : '68px'}` }}>
                <PodSpacer padding={['TOP', 'BOTTOM']}>
                  <ProductHeader
                    brand="above"
                    itemId={pod.itemId}
                    brandTitleMaxLine={3}
                    disableShopThisCollection
                    onClick={handleClickOnProductHeader}
                  />
                </PodSpacer>
              </div>
              <PodSpacer minHeight="26px">
                <div ref={productPodRatingRef}>
                  <ProductRating itemId={pod.itemId} />
                </div>
              </PodSpacer>
              <PodSpacer minHeight="60px">
                <Price
                  itemId={pod.itemId}
                  large={false}
                  storeId={storeId}
                  displayEachUom={false}
                  onSavingsCenterToggle={pod.onSavingsCenterToggle}
                  hideBadge
                />
              </PodSpacer>
            </PodSection>
            {!isMobile && (
              <PodFooter>
                <PodSpacer minHeight="40px" padding={['TOP']}>
                  <ProductATC
                    itemId={pod.itemId}
                    storeId={storeId}
                    checkGeneric
                    outline
                    doNotShowChooseYourOptions
                    noATCFulfillment={noATCFulfillment}
                    onClick={handleClickOnProductAtc}
                  />
                </PodSpacer>
              </PodFooter>
            )}
          </>
        )}
      />
    </div>
  );
};

SponsoredProductPod.propTypes = {
  storeId: string.isRequired,
  product: shape({
    itemId: string.isRequired,
    identifiers: shape({
      canonicalUrl: string,
    }),
    info: shape({
      sponsoredBeacon: shape({
        onClickBeacons: arrayOf(string),
        onViewBeacons: arrayOf(string),
      }),
      sponsoredMetadata: shape({
        trackSource: string,
        campaignId: string,
        placementId: string,
        slotId: string,
        sponsoredId: string,
      }),
    }),
  }).isRequired,
  position: number.isRequired,
  noATCFulfillment: bool,
};

SponsoredProductPod.defaultProps = {
  noATCFulfillment: null,
};

SponsoredProductPod.dataModel = dataModel;

export { SponsoredProductPod };
